import { NumericInput } from '@/components/inputs/NumericInput'
import { TextInput } from '@/components/inputs/TextInput'
import {
  type Choice,
  ChoiceSelect,
} from '@/features/legends/components/ChoiceSelect'
import type { AnswerOption } from '@/features/legends/editor/entities'
import { type RynoType, Types } from '@/features/legends/editor/ryno-types'

/**
 * A value a {@link ValueInput} edits.
 */
export type InputValue = string | number | boolean | null

export interface ValueInputProps {
  type: RynoType
  value: InputValue
  onChange: (value: InputValue) => unknown
  /**
   * When the target has known answers, the value is picked from them instead.
   */
  options?: AnswerOption[] | null
  isDisabled?: boolean
  isInvalid?: boolean
  testId?: string
}

/**
 * An input for a value of the type: text, a number or yes/no.
 */
export function ValueInput({
  type,
  value,
  onChange,
  options,
  isDisabled,
  isInvalid,
  testId,
}: ValueInputProps) {
  const status = isInvalid ? 'danger' : 'default'

  if (options || Types.isBoolType(type)) {
    const choices: Choice<InputValue>[] = options
      ? options.map((o) => [o.value, o.text])
      : [
          [true, 'Yes'],
          [false, 'No'],
        ]
    return (
      <ChoiceSelect
        value={Types.isBoolType(type) ? !!value : value}
        choices={choices}
        onChange={onChange}
        placeholder={options ? 'Select a value' : undefined}
        isDisabled={isDisabled}
        isInvalid={isInvalid}
        testId={testId}
      />
    )
  }

  if (Types.isNumberType(type)) {
    return (
      <NumericInput
        value={typeof value === 'number' ? value : null}
        onChange={(number) => onChange(number ?? null)}
        precision={10}
        fixedPrecision={false}
        allowNegative
        placeholder="0"
        isDisabled={isDisabled}
        status={status}
        testId={testId}
      />
    )
  }

  if (Types.isStringType(type)) {
    return (
      <TextInput
        value={typeof value === 'string' ? value : ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Enter a value"
        isDisabled={isDisabled}
        status={status}
        data-test={testId}
      />
    )
  }

  return <TextInput value="Not applicable" isDisabled data-test={testId} />
}
